import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { ProviderApi, NotificationApi } from 'services';
import { toastMessages } from 'config/constants';
import get from 'lodash/get';
import toast from 'react-hot-toast';
import useGenericContext from 'hooks/useGenericContext';

const ProviderContext = createContext(undefined);
const ProviderFunctionsContext = createContext(undefined);

function ProviderProvider({ children }) {
  const [notifications, setNotifications] = useState([false, false, false, false, false, false, false, false]);
  const [profilePicture, setProfilePicture] = useState(null);
  const [provider, setProvider] = useState(null);
  const [providerLoading, setProviderLoading] = useState(true);
  const [providerSaving, setProviderSaving] = useState(false);

  const [isFetchingNotifications, setIsFetchingNotifications] = useState(true);
  const [isFetchingProvider, setIsFetchingProvider] = useState(true);

  const loadProviderData = useCallback(() => {
    ProviderApi.getCurrentProvider()
      .then(({ data }) => {
        setProvider(data);
        setIsFetchingProvider(false);
      })
      .catch((e) => {
        if (get(e, 'response.status') === 401) toast.error(toastMessages.error.SESSION_EXPIRED);
        else toast.error(toastMessages.error.FETCH_PROVIDER);
        setIsFetchingProvider(false);
      });
  }, []);

  const loadNotifications = useCallback(() => {
    NotificationApi.listNotifications()
      .then(({ data }) => {
        setNotifications([
          data.email_booking,
          data.sms_booking,
          data.email_agenda,
          data.sms_agenda,
          data.email_employee_deleted,
          data.sms_employee_deleted,
          data.email_company_deleted,
          data.sms_company_deleted,
        ]);
        setIsFetchingNotifications(false);
      })
      .catch((e) => {
        if (get(e, 'response.status') !== 401) toast.error(toastMessages.error.FETCH_NOTIFICATIONS);
        setIsFetchingNotifications(false);
      });
  }, []);

  const loadProfilePicture = useCallback(() => {
    ProviderApi.getPicture()
      .then(({ url }) => {
        setProfilePicture(url);
      })
      .catch((e) => {
        if (get(e, 'response.status') !== 401 && get(e, 'response.status') !== 404)
          toast.error(toastMessages.error.FETCH_PROFILE_PICTURE);
      });
  }, []);

  const loadProvider = useCallback(() => {
    loadProviderData();
    loadNotifications();
    loadProfilePicture();
  }, [loadNotifications, loadProfilePicture, loadProviderData]);

  useEffect(() => {
    loadProvider();
  }, [loadProvider]);

  useEffect(() => {
    if (!isFetchingNotifications && !isFetchingProvider) setProviderLoading(false);
  }, [isFetchingNotifications, isFetchingProvider]);

  const editNotifications = useCallback(
    async (data) => {
      setProviderSaving(true);
      setNotifications(data);

      const oldNotifications = [...notifications];
      try {
        await NotificationApi.updateNotifications({ notifications: data });
        toast.success(toastMessages.success.UPDATE_NOTIFICATIONS);
      } catch (e) {
        setNotifications(oldNotifications);
        toast.error(toastMessages.error.UPDATE_NOTIFICATIONS);
      }
    },
    [notifications],
  );

  const editProvider = useCallback(async (data) => {
    setProviderSaving(true);

    try {
      const { data: provider } = await ProviderApi.editProvider(data);

      const newAddressEntity = { ...provider.addressEntity, city_name: data.city };
      setProvider({ ...provider, addressEntity: newAddressEntity });
      setProviderSaving(false);

      toast.success(toastMessages.success.UPDATE_PROVIDER);
      return true;
    } catch (e) {
      setProviderSaving(false);
      toast.error(toastMessages.error.UPDATE_PROVIDER);
      return false;
    }
  }, []);

  const editProfilePicture = useCallback(
    async (file, data) => {
      const fileURL = URL.createObjectURL(file);
      const oldFile = profilePicture;
      ProviderApi.updatePicture(data)
        .then(() => {
          toast.success(toastMessages.success.UPDATE_PROFILE_PICTURE);
        })
        .catch(() => {
          setProfilePicture(oldFile);
          toast.error(toastMessages.error.UPDATE_PROFILE_PICTURE);
        });
      setProfilePicture(fileURL);
    },
    [profilePicture],
  );

  const value = useMemo(
    () => ({
      notifications,
      profilePicture,
      provider,
      providerLoading,
      providerSaving,
    }),
    [notifications, profilePicture, provider, providerLoading, providerSaving],
  );

  const functions = useMemo(
    () => ({
      editNotifications,
      editProfilePicture,
      editProvider,
    }),
    [editNotifications, editProfilePicture, editProvider],
  );

  return (
    <ProviderContext.Provider value={value}>
      <ProviderFunctionsContext.Provider value={functions}>{children}</ProviderFunctionsContext.Provider>
    </ProviderContext.Provider>
  );
}

/**
 * Return the items values.
 * @return {{ provider: Object, providerLoading: boolean }}
 */
function useProvider() {
  return useGenericContext(ProviderContext, 'useProvider cannot be used outside of a ProviderProvider component.');
}

/**
 * Return the functions manipulating items.
 * @return {{ editProvider: Function }}
 */
function useProviderFunctions() {
  return useGenericContext(
    ProviderFunctionsContext,
    'useProviderFunctions cannot be used outside of a ProviderProvider component',
  );
}

export { ProviderProvider, useProvider, useProviderFunctions };
